import React, { useState, useEffect } from "react";
import { Spin, Steps, Typography, Row, Tooltip } from "antd";
import "../../assets/dist/NewApplication/ApplicantPreview.css";
import _NewapplicationService from "../../modules/NewapplicationService";
import "./Dashboard.scss";
import ApplicationWorkFlow from "./ApplicationWorkFlow";
const { Step } = Steps;
const { Text } = Typography;

const ApplicationStatusTab = ({ activeKey }) => {

  const [applicationStatusSteps, setApplicationStatusSteps] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeKey && activeKey == "1") {
      getApplicationStatusByApplicationId(Number(localStorage.getItem("ApplicationObjId")));
    }
  }, [activeKey]);

  const getApplicationStatusByApplicationId = async (applicationObjId) => {
    setLoading(true);
    try {
      const res = await _NewapplicationService.getApplicationStatusByApplicationId(Number(applicationObjId));
      if (res.data) {
        let resObj = res.data.Obj;
        setApplicationStatusSteps(resObj);
        localStorage.setItem("isAllStepsCompleted", JSON.stringify(resObj.applicationScreenStatus.IsAllStepsCompleted ?? false));
        setLoading(false);
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  // const hasTrueOrFalse = Object.values(applicationStatusSteps && applicationStatusSteps.applicationScreenStatus).some(
  //   value => value === true || value === false
  // );

  // Final outcome titles the API may return once a decision has been made.
  const FINAL_OUTCOME_TITLES = ["Approved", "Rejected", "Declined"];
  // Statuses that mark a stage as negative/problematic (styled red).
  const NEGATIVE_STATUS_TITLES = ["Action Required", "Rejected", "Declined"];

  // UI-only grouping: everything except Draft / Document Upload / Submitted / a final
  // outcome (Approved, Rejected, Declined) is collapsed into a single "In Loan Review"
  // step (covers In Progress, In Review Underwriter Banker, Propose, In Review Private
  // Banker, Recommended, In Review Loan Officer, Action Required, etc.), with the
  // individual statuses shown in a hover tooltip. The API response itself is untouched.
  const STANDALONE_STEP_TITLES = ["Draft", "Document Upload", "Submitted", ...FINAL_OUTCOME_TITLES];

  const groupStatusStepsForDisplay = (steps) => {
    if (!steps || steps.length === 0) return [];
    const grouped = [];
    let buffer = [];

    const flushBuffer = () => {
      if (buffer.length > 0) {
        const lastBufferedItem = buffer[buffer.length - 1];
        grouped.push({
          isGroup: true,
          Title: "In Loan Review",
          Date: lastBufferedItem.Date,
          Status: lastBufferedItem.Status,
          items: buffer,
        });
        buffer = [];
      }
    };

    steps.forEach((step) => {
      if (STANDALONE_STEP_TITLES.includes(step.Title)) {
        flushBuffer();
        grouped.push({ isGroup: false, ...step });
      } else {
        buffer.push(step);
      }
    });
    flushBuffer();

    return grouped;
  };

  const groupedStatusSteps = groupStatusStepsForDisplay(applicationStatusSteps?.ListApplicantStatusModel);

  // Always render a fixed 5-stage timeline (Draft, Document Upload, Submitted,
  // In Loan Review, <final outcome>) regardless of how many statuses the API
  // has returned so far. Stages not yet reached are rendered as hollow
  // placeholders; the API response and existing grouping logic above are
  // untouched. The 5th stage's title is driven entirely by the API response:
  // it defaults to "Approved" as a placeholder until the applicant's current
  // status is actually one of the final outcomes, at which point it switches
  // to whatever the API returned (Approved / Rejected / Declined).
  const lastGroupedStep = groupedStatusSteps.length > 0 ? groupedStatusSteps[groupedStatusSteps.length - 1] : null;
  const finalOutcomeTitle =
    lastGroupedStep && FINAL_OUTCOME_TITLES.includes(lastGroupedStep.Title) ? lastGroupedStep.Title : "Approved";

  const ALL_STAGE_TITLES = ["Draft", "Document Upload", "Submitted", "In Loan Review", finalOutcomeTitle];

  const currentStageIndex = lastGroupedStep ? ALL_STAGE_TITLES.indexOf(lastGroupedStep.Title) : -1;
  const isFinalStage = currentStageIndex === ALL_STAGE_TITLES.length - 1;

  const fixedStatusSteps = ALL_STAGE_TITLES.map((title, idx) => {
    const matched = groupedStatusSteps.find((s) => s.Title === title);

    let stageState = "wait";
    if (currentStageIndex !== -1) {
      if (idx < currentStageIndex) stageState = "finish";
      else if (idx === currentStageIndex) stageState = isFinalStage ? "finish" : "process";
    }

    const lastItem = matched?.isGroup ? matched.items[matched.items.length - 1] : matched;
    const isNegativeOutcome =
      idx === currentStageIndex && !!lastItem && NEGATIVE_STATUS_TITLES.includes(lastItem.Title);

    return {
      isGroup: false,
      Title: title,
      Date: "",
      Status: "wait",
      ...matched,
      stageState,
      isNegativeOutcome,
    };
  });

  const currentStageTitle = currentStageIndex >= 0 ? ALL_STAGE_TITLES[currentStageIndex] : null;

  return (
    <Spin spinning={loading}>

      <div className="card-section">
        <Row className="workflowRow">
          <ApplicationWorkFlow activeKey={activeKey} applicationStatusSteps={applicationStatusSteps} />
        </Row>

        {currentStageTitle && (
          <div className="applicationStatusProgressLabel mt10">
            <Text type="secondary">
              Status: <Text strong>{currentStageTitle}</Text> &middot; Step {currentStageIndex + 1} of {ALL_STAGE_TITLES.length}
            </Text>
          </div>
        )}

        <Steps
          current={1}
          labelPlacement="vertical"
          size="small"

          className="applicationStatusTimeline applicationStatusTimelineMain card-section mt10 "
        >

          {fixedStatusSteps?.map((step, index) => {
            const lastItem = step.isGroup ? step.items[step.items.length - 1] : step;
            const isNegativeOutcome = step.isNegativeOutcome;

            const titleNode = (
              <Text
                strong
                style={{
                  color: isNegativeOutcome
                    ? "red"
                    : step.stageState === "wait"
                      ? "#999"
                      : step.stageState === "process"
                        ? "#FAAD14"
                        : "#00E3C4",
                }}
              >
                {step.Title}
              </Text>
            );

            const stepDescription =
              step.stageState === "wait"
                ? "Remaining"
                : step.stageState === "process"
                  ? (isNegativeOutcome ? lastItem?.Title : "In progress")
                  : step.Date;

            return (
              <Step
                key={index}
                title={
                  step.isGroup ? (
                    <Tooltip
                      overlayStyle={{ maxWidth: "none" }}
                      overlayInnerStyle={{ padding: 16 }}
                      title={
                        <Steps
                          size="small"
                          labelPlacement="vertical"
                          className="applicationStatusTimeline"
                          style={{ minWidth: step.items.length * 130 }}
                        >
                          {step.items.map((item, itemIndex) => (
                            <Step
                              key={itemIndex}
                              title={
                                <Text
                                  strong
                                  style={{ color: item.Status === "wait" ? "#999" : "#00E3C4" }}
                                >
                                  {item.Title}
                                </Text>
                              }
                              description={<Text type="secondary">{item.Date}</Text>}
                              status={item.Status === "wait" ? "wait" : "finish"}
                            />
                          ))}
                        </Steps>
                      }
                    >
                      <div>{titleNode}</div>
                    </Tooltip>
                  ) : (
                    <div>{titleNode}</div>
                  )
                }
                description={<Text type="secondary">{stepDescription}</Text>}
                status={isNegativeOutcome ? "error" : step.stageState}
                className="horizontal-scroll"
              />
            );
          })}
        </Steps>

      </div>
    </Spin>
  );
};

export default ApplicationStatusTab;